import { jsx, jsxs } from "react/jsx-runtime";
import "@tamagui/polyfill-dev";
import { Adapt, useAdaptParent } from "@tamagui/adapt";
import { AnimatePresence } from "@tamagui/animate-presence";
import { hideOthers } from "@tamagui/aria-hidden";
import { useComposedRefs } from "@tamagui/compose-refs";
import {
  Stack,
  Theme,
  composeEventHandlers,
  isWeb,
  useEvent,
  useGet,
  useMedia,
  useThemeName,
  withStaticProperties
} from "@tamagui/core";
import { createContextScope } from "@tamagui/create-context";
import { FloatingOverrideContext } from "@tamagui/floating";
import { FocusScope } from "@tamagui/focus-scope";
import {
  Popper,
  PopperAnchor,
  PopperArrow,
  PopperContent,
  PopperContentFrame,
  PopperProvider,
  createPopperScope,
  usePopperContext
} from "@tamagui/popper";
import { Portal, PortalHost, PortalItem } from "@tamagui/portal";
import { RemoveScroll } from "@tamagui/remove-scroll";
import { Sheet, SheetController } from "@tamagui/sheet";
import { YStack } from "@tamagui/stacks";
import { useControllableState } from "@tamagui/use-controllable-state";
import * as React from "react";
import { Platform, ScrollView } from "react-native";
import { useFloatingContext } from "./useFloatingContext";
const POPOVER_NAME = "Popover";
const [createPopoverContext, createPopoverScopeInternal] = createContextScope(
  POPOVER_NAME,
  [createPopperScope]
);
const usePopoverScope = createPopperScope();
const createPopoverScope = createPopoverScopeInternal;
const [PopoverProviderInternal, usePopoverInternalContext] = createPopoverContext(POPOVER_NAME);
const __PopoverProviderInternal = PopoverProviderInternal;
const ANCHOR_NAME = "PopoverAnchor";
const PopoverAnchor = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopePopover, ...anchorProps } = props;
    const context = usePopoverInternalContext(ANCHOR_NAME, __scopePopover);
    const popperScope = usePopoverScope(__scopePopover);
    const { onCustomAnchorAdd, onCustomAnchorRemove } = context;
    React.useEffect(() => {
      onCustomAnchorAdd();
      return () => onCustomAnchorRemove();
    }, [onCustomAnchorAdd, onCustomAnchorRemove]);
    return /* @__PURE__ */ jsx(PopperAnchor, { ...popperScope, ...anchorProps, ref: forwardedRef });
  }
);
PopoverAnchor.displayName = ANCHOR_NAME;
const TRIGGER_NAME = "PopoverTrigger";
const PopoverTrigger = React.forwardRef((props, forwardedRef) => {
  const { __scopePopover, ...triggerProps } = props;
  const context = usePopoverInternalContext(TRIGGER_NAME, __scopePopover);
  const popperScope = usePopoverScope(__scopePopover);
  const composedTriggerRef = useComposedRefs(forwardedRef, context.triggerRef);
  const trigger = /* @__PURE__ */ jsx(
    YStack,
    {
      "aria-haspopup": "dialog",
      "aria-expanded": context.open,
      "data-state": getState(context.open),
      ...triggerProps,
      ref: composedTriggerRef,
      onPress: composeEventHandlers(props.onPress, context.onOpenToggle)
    }
  );
  return context.hasCustomAnchor ? trigger : /* @__PURE__ */ jsx(PopperAnchor, { asChild: true, ...popperScope, children: trigger });
});
PopoverTrigger.displayName = TRIGGER_NAME;
const CONTENT_NAME = "PopoverContent";
const PopoverContent = PopperContentFrame.extractable(
  React.forwardRef(
    function PopoverContent2(props, forwardedRef) {
      const {
        allowPinchZoom,
        trapFocus,
        disableRemoveScroll = true,
        zIndex,
        ...contentImplProps
      } = props;
      const context = usePopoverInternalContext(CONTENT_NAME, props.__scopePopover);
      const contentRef = React.useRef(null);
      const composedRefs = useComposedRefs(forwardedRef, contentRef);
      const isRightClickOutsideRef = React.useRef(false);
      React.useEffect(() => {
        if (!context.open)
          return;
        const content = contentRef.current;
        if (content)
          return hideOthers(content);
      }, [context.open]);
      return /* @__PURE__ */ jsx(PopoverContentPortal, { zIndex, children: /* @__PURE__ */ jsx(
        PopoverContentImpl,
        {
          ...contentImplProps,
          disableRemoveScroll,
          ref: composedRefs,
          trapFocus: trapFocus ?? context.open,
          disableOutsidePointerEvents: true,
          onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, (event) => {
            var _a;
            event.preventDefault();
            if (!isRightClickOutsideRef.current)
              (_a = context.triggerRef.current) == null ? void 0 : _a.focus();
          }),
          onPointerDownOutside: composeEventHandlers(
            props.onPointerDownOutside,
            (event) => {
              const originalEvent = event.detail.originalEvent;
              const ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
              const isRightClick = originalEvent.button === 2 || ctrlLeftClick;
              isRightClickOutsideRef.current = isRightClick;
            },
            { checkDefaultPrevented: false }
          ),
          onFocusOutside: composeEventHandlers(
            props.onFocusOutside,
            (event) => event.preventDefault(),
            { checkDefaultPrevented: false }
          )
        }
      ) });
    }
  )
);
function PopoverContentPortal(props) {
  const themeName = useThemeName();
  const context = usePopoverInternalContext(CONTENT_NAME, props.__scopePopover);
  let contents = props.children;
  if (Platform.OS === "android") {
    const popperContext = usePopperContext(CONTENT_NAME, context.popperScope);
    contents = /* @__PURE__ */ jsx(PopperProvider, { ...popperContext, scope: context.popperScope, children: /* @__PURE__ */ jsx(PopoverProviderInternal, { scope: props.__scopePopover, ...context, children: props.children }) });
  }
  const zIndex = props.zIndex ?? 15e4;
  return /* @__PURE__ */ jsx(Portal, { zIndex, children: /* @__PURE__ */ jsxs(Theme, { forceClassName: true, name: themeName, children: [
    !!context.open && !context.breakpointActive && /* @__PURE__ */ jsx(
      YStack,
      {
        fullscreen: true,
        onPress: composeEventHandlers(props.onPress, context.onOpenToggle)
      }
    ),
    /* @__PURE__ */ jsx(Stack, { zIndex: zIndex + 1, children: contents })
  ] }) });
}
const PopoverContentImpl = React.forwardRef((props, forwardedRef) => {
  const {
    __scopePopover,
    trapFocus,
    onOpenAutoFocus,
    onCloseAutoFocus,
    disableOutsidePointerEvents,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    children,
    disableRemoveScroll,
    ...contentProps
  } = props;
  const popperScope = usePopoverScope(__scopePopover);
  const context = usePopoverInternalContext(CONTENT_NAME, popperScope.__scopePopover);
  const [isFullyHidden, setIsFullyHidden] = React.useState(!context.open);
  if (context.breakpointActive) {
    const childrenWithoutScrollView = React.Children.toArray(children).map((child) => {
      if (React.isValidElement(child)) {
        if (child.type === PopoverScrollView) {
          return child.props.children;
        }
      }
      return child;
    });
    return /* @__PURE__ */ jsx(PortalItem, { hostName: `${context.scopeKey}PopoverContents`, children: childrenWithoutScrollView });
  }
  if (context.open && isFullyHidden) {
    setIsFullyHidden(false);
  }
  if (isFullyHidden) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    AnimatePresence,
    {
      onExitComplete: () => {
        setIsFullyHidden(true);
      },
      children: !!context.open && /* @__PURE__ */ jsx(
        PopperContent,
        {
          "data-state": getState(context.open),
          id: context.contentId,
          pointerEvents: "auto",
          ref: forwardedRef,
          ...popperScope,
          ...contentProps,
          children: /* @__PURE__ */ jsx(
            RemoveScroll,
            {
              enabled: disableRemoveScroll ? false : context.open,
              allowPinchZoom: true,
              removeScrollBar: false,
              style: {
                display: "contents"
              },
              children: trapFocus === false ? children : /* @__PURE__ */ jsx(
                FocusScope,
                {
                  loop: true,
                  trapped: trapFocus ?? context.open,
                  onMountAutoFocus: onOpenAutoFocus,
                  onUnmountAutoFocus: onCloseAutoFocus,
                  children: isWeb ? /* @__PURE__ */ jsx("div", { style: { display: "contents" }, children }) : children
                }
              )
            }
          )
        },
        context.contentId
      )
    }
  );
});
const CLOSE_NAME = "PopoverClose";
const PopoverClose = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopePopover, ...closeProps } = props;
    const context = usePopoverInternalContext(CLOSE_NAME, __scopePopover);
    return /* @__PURE__ */ jsx(
      YStack,
      {
        ...closeProps,
        ref: forwardedRef,
        onPress: composeEventHandlers(
          props.onPress,
          () => context.onOpenChange(false)
        )
      }
    );
  }
);
PopoverClose.displayName = CLOSE_NAME;
const ARROW_NAME = "PopoverArrow";
const PopoverArrow = PopperArrow.styleable(
  (props, forwardedRef) => {
    const { __scopePopover, ...arrowProps } = props;
    const popperScope = usePopoverScope(__scopePopover);
    return /* @__PURE__ */ jsx(PopperArrow, { ...popperScope, ...arrowProps, ref: forwardedRef });
  }
);
PopoverArrow.displayName = ARROW_NAME;
const PopoverScrollView = React.forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(ScrollView, { ref, ...props });
});
const Popover = withStaticProperties(
  function Popover2(props) {
    const {
      __scopePopover,
      children,
      open: openProp,
      defaultOpen,
      onOpenChange,
      ...restProps
    } = props;
    const internalId = React.useId();
    const id = __scopePopover ? Object.keys(__scopePopover)[0] : internalId;
    const { when, AdaptProvider } = useAdaptParent({
      Contents: React.useCallback(() => {
        return /* @__PURE__ */ jsx(PortalHost, { name: `${id}PopoverContents` });
      }, [])
    });
    const sheetBreakpoint = when;
    const popperScope = usePopoverScope(__scopePopover);
    const triggerRef = React.useRef(null);
    const [hasCustomAnchor, setHasCustomAnchor] = React.useState(false);
    const [open, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen || false,
      onChange: onOpenChange,
      transition: true
    });
    const breakpointActive = useSheetBreakpointActive(sheetBreakpoint);
    const floatingContext = useFloatingContext({ open, setOpen, breakpointActive });
    const popoverContext = {
      scope: __scopePopover,
      scopeKey: id,
      popperScope: popperScope.__scopePopper,
      sheetBreakpoint,
      contentId: React.useId(),
      triggerRef,
      open,
      breakpointActive,
      onOpenChange: setOpen,
      onOpenToggle: useEvent(() => {
        if (open && breakpointActive) {
          return;
        }
        setOpen(!open);
      }),
      hasCustomAnchor,
      onCustomAnchorAdd: React.useCallback(() => setHasCustomAnchor(true), []),
      onCustomAnchorRemove: React.useCallback(() => setHasCustomAnchor(false), [])
    };
    const contents = /* @__PURE__ */ jsx(Popper, { ...popperScope, stayInFrame: true, ...restProps, children: /* @__PURE__ */ jsx(PopoverProviderInternal, { ...popoverContext, children: /* @__PURE__ */ jsx(PopoverSheetController, { onOpenChange: setOpen, __scopePopover, children }) }) });
    return /* @__PURE__ */ jsx(AdaptProvider, { children: isWeb ? /* @__PURE__ */ jsx(FloatingOverrideContext.Provider, { value: floatingContext, children: contents }) : contents });
  },
  {
    Anchor: PopoverAnchor,
    Arrow: PopoverArrow,
    Trigger: PopoverTrigger,
    Content: PopoverContent,
    Close: PopoverClose,
    Adapt,
    ScrollView: PopoverScrollView,
    Sheet: Sheet.Controlled
  }
);
function getState(open) {
  return open ? "open" : "closed";
}
const PopoverSheetController = (props) => {
  const context = usePopoverInternalContext(
    "PopoverSheetController",
    props.__scopePopover
  );
  const showSheet = useShowPopoverSheet(context);
  const breakpointActive = context.breakpointActive;
  const getShowSheet = useGet(showSheet);
  return /* @__PURE__ */ jsx(
    SheetController,
    {
      onOpenChange: (val) => {
        if (getShowSheet()) {
          props.onOpenChange(val);
        }
      },
      open: context.open,
      hidden: breakpointActive === false,
      children: props.children
    }
  );
};
const useSheetBreakpointActive = (breakpoint) => {
  const media = useMedia();
  if (typeof breakpoint === "boolean" || !breakpoint) {
    return !!breakpoint;
  }
  return media[breakpoint];
};
const useShowPopoverSheet = (context) => {
  const breakpointActive = useSheetBreakpointActive(context.sheetBreakpoint);
  return context.open === false ? false : breakpointActive;
};
export {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  __PopoverProviderInternal,
  createPopoverScope,
  usePopoverScope
};
//# sourceMappingURL=Popover.js.map
